// @flow
import * as React from "react";
import {reisy, use} from "reisy";

import {normalizeStyle, isTiledItem} from "../api/items.js";
import type {Styles, PositionItem, ItemPublishable, ItemSnippet} from "../api/items.js";
import type {Content} from "../api/types.js";
import Publishable from "./index.js";
import Link from "../components/link.js";

const messages = {
	empty: {
		id: "flow.empty",
		defaultMessage: "There is no content to display here.",
	},
	showCollection: {
		id: 'more.showCollection',
		defaultMessage: 'show all stories',
	},
	showMore: {
		id: 'more.showMore',
		defaultMessage: 'show more stories',
	},
	showMoreBack: {
		id: 'more.showMoreBack',
		defaultMessage: '«',
	},
	showMoreSearch: {
		id: 'more.showMoreSearch',
		defaultMessage: 'show more results',
	},
	showMoreSearchBack: {
		id: 'more.showMoreSearchBack',
		defaultMessage: '«',
	},
};

export const styles = reisy`
@namespace Flow;
SectionInSection: false;
mobileLayout: $Grid.mobileLayout;

Empty {
	font-family: $Fonts.text;
	margin: 0;
	padding: 4em 2em 2em;
	color: $Colors.TextSubtle;
	text-align: center;
}

Container {
	@extends Grid.container;
}

autoMobileLayout {
	@extends Grid.autoMobileLayout;
}
keepMobileLayout {
	@extends Grid.keepMobileLayout;
}
tileMobileLayout {
	@extends Grid.tileMobileLayout;
}

ReadMore {
	display: block;
	text-align: center;
	width: 100%;
	font-family: $Fonts.aside;
	position: relative;
	padding: 2rem 0;
	border-bottom: 1px solid #ddd;
}
ReadMoreButton {
	padding: .75em 2em;
	border: 1px solid rgba(0, 0, 0, 0.4);
	color: #000;
	text-decoration: none;
	display: inline-block;
	vertical-align: top;
	background: #fff;

	&:hover {
		background: #f0f0f0;
	}
}

ReadMoreBackButton {
	@extends ReadMoreButton;
	margin-right: 1em;
}
`;

const settings = reisy.namespace('Settings');

export function hasSectionInSection(publisher = {}) {
	if (styles.SectionInSection === undefined) { //in case reisy isn't fully initialized yet
		const { appearance: { styles = {} } = {} } = publisher;
		const { def } = styles.find(s => s.ns === 'Flow' && s.name === 'SectionInSection') || {};
		return !!def;
	} else {
		return styles.SectionInSection;
	}
}

function getItemStyle(layout: Array<string>, item: ItemPublishable, hasParent: boolean = false): ?Styles {
	// Content Item options always take precedence
	if (item.options.style) {
		return item.options.style;
	}

	// *toplevel* collections (those not inside another section) are always
	// displayed as `section`.
	if (item.content.type === "collection" && (!hasParent || styles.SectionInSection && !item.options.style && item.options.layout)) {
		return "section";
	}

	layout.currentIndex = layout.currentIndex || 0;

	let style = layout[layout.currentIndex];
	if (layout.currentIndex < layout.length - 1) {
		layout.currentIndex += 1;
	} else if (style === 'loop') {
		layout.currentIndex = 1;
		style = layout[0];
	}

	return normalizeStyle(item.content.type, style);
}

class Snippet extends React.Component {
	props: {item: ItemSnippet};
	elem: HTMLElement;
	setElem = (elem: HTMLElement) => (this.elem = elem);

	componentDidMount() {
		if (!IS_BROWSER) { return; }
		const {elem} = this;

		// This will collect all the script snippets into one block of code, and
		// then inject is as a manually created script tag, so it is properly
		// executed by the browser.
		const scripts = elem.querySelectorAll("script");
		let code = "";
		for (let i = 0; i < scripts.length; i++) {
			const script = scripts[i];
			// $FlowFixMe: its a script alright
			code += `;${script.text}`;
			// $FlowFixMe: it does have a parent
			script.parentNode.removeChild(script);
		}
		const script = document.createElement("script");
		script.text = code;
		elem.appendChild(script);
	}

	render() {
		const {item} = this.props;
		return (<div ref={this.setElem}
			className={item.options.className}
			dangerouslySetInnerHTML={{__html: item.content}} />);
	}
}

const Flow = (_layout: ?string, formatMessage: any, items: ?Array<PositionItem>, _parent: boolean | Content = false, hierarchy: Array, overrideMessages: Object) => {
	const msgs = Object.assign({}, messages, overrideMessages);

	if (!items || !items.length) {
		return (<p {...use(styles.Empty)}>{formatMessage(msgs.empty)}</p>);
	}

	const parent = typeof _parent === "object" ? _parent : null;

	const main = [];
	let flow = [];
	const finishFlow = i => {
		if (!flow.length) { return; }
		main.push(<section key="container" {...use(styles.Container, styles[`${styles.mobileLayout}MobileLayout`])}>{flow}</section>);
		flow = [];
	};

	// if no explicit layout is set, default to "tile"
	const layout = (_layout.layout || _layout || "tile").split(",").map(s => s.trim());

	for (let i = 0; i < items.length; i++) {
		const item = items[i];
		let rendered, wasTiled = isTiledItem(item);
		if (item.type === "publishable") {
			const style = getItemStyle(layout, item, Boolean(parent));
			wasTiled = isTiledItem(item, style);
			rendered = (<Publishable index={i} key={i} {...item} style={style} parent={parent} hierarchy={hierarchy} />);
		} else if (item.type === "snippet") {
			rendered = (<Snippet index={i} key={i} item={item} />);
		} else if (item.type === 'pagenav') {
			if(item.contentType === 'user' || item.contentType === 'publisher'){
				rendered = (<section key="pagenav" {...use(styles.ReadMore)}>
					{item.prev ? <Link {...use(styles.ReadMoreBackButton)} to={{query: {page: item.page - 1}}} >{formatMessage(msgs.showMoreBack)}</Link> : null }
					{item.next ? <Link {...use(styles.ReadMoreButton)} to={{query: {page: item.page + 1}}} triggerInViewport={item.page > 1 && settings.AppendPaging} >{formatMessage(msgs.showMore)}</Link> : null }
				</section>);
			} else if(item.contentType === 'search'){
				rendered = (<section key="pagenav"  {...use(styles.ReadMore)}>
					{item.prev ? <Link {...use(styles.ReadMoreBackButton)} to={{search: item.content, query: {page: item.page - 1}}} >{formatMessage(msgs.showMoreSearchBack)}</Link> : null }
					{item.next ? <Link {...use(styles.ReadMoreButton)} to={{search: item.content, query: {page: item.page + 1}}} >{formatMessage(msgs.showMoreSearch)}</Link> : null }
				</section>);
			} else if(item.contentType === 'topliked'){
				rendered = (<section key="pagenav"  {...use(styles.ReadMore)}>
					{item.prev ? <Link {...use(styles.ReadMoreBackButton)} to={{topliked: item.content, query: {page: item.page - 1}}} >{formatMessage(msgs.showMoreBack)}</Link> : null }
					{item.next ? <Link {...use(styles.ReadMoreButton)} to={{topliked: item.content, query: {page: item.page + 1}}} >{formatMessage(msgs.showMore)}</Link> : null }
				</section>);
			} else if(item.contentType === 'tagged'){
				rendered = (<section key="pagenav"  {...use(styles.ReadMore)}>
					{item.prev ? <Link {...use(styles.ReadMoreBackButton)} to={{tagged: item.content, query: {page: item.page - 1}}} >{formatMessage(msgs.showMoreBack)}</Link> : null }
					{item.next ? <Link {...use(styles.ReadMoreButton)} to={{tagged: item.content, query: {page: item.page + 1}}} >{formatMessage(msgs.showMore)}</Link> : null }
				</section>);
			} else {
				rendered = (<section key="more"  {...use(styles.ReadMore)}>
					<Link {...use(styles.ReadMoreButton)} to={{append: [item.content]}} >{formatMessage(msgs.showCollection)}</Link>
				</section>);
			}
		}

		if (!rendered) { continue; }

		if (wasTiled) {
			flow.push(rendered);
		} else {
			finishFlow(i - 1);
			main.push(rendered);
		}
	}
	finishFlow(items.length);
	return main;
};

export default Flow;



// WEBPACK FOOTER //
// ./lib/publishables/flow.js